<template>
  <div>
    <Teleport to="body">
      <div v-if="showLightBox" class="vel-modal" @click.self="onHideLightBox">
        <div class="vel-top-controls">
          <button class="vel-ctrl-btn" :aria-label="isPlaying ? 'Stop slideshow' : 'Start slideshow'" @click="toggleSlideshow">
            <svg v-if="!isPlaying" viewBox="0 0 24 24" fill="white" width="20" height="20">
              <path d="M8 5v14l11-7z" />
            </svg>
            <svg v-else viewBox="0 0 24 24" fill="white" width="20" height="20">
              <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
            </svg>
          </button>
          <button class="vel-ctrl-btn" :aria-label="showThumbnails ? 'Hide thumbnails' : 'Show thumbnails'" @click="toggleThumbnails">
            <svg viewBox="0 0 24 24" fill="white" width="20" height="20">
              <path d="M3 3h7v7H3V3zm0 11h7v7H3v-7zm11-11h7v7h-7V3zm0 11h7v7h-7v-7z" />
            </svg>
          </button>
          <button class="vel-ctrl-btn" aria-label="Close" @click="onHideLightBox">
            <svg viewBox="0 0 24 24" fill="none" width="20" height="20">
              <path d="M18 6L6 18M6 6l12 12" stroke="white" stroke-width="2" stroke-linecap="round" />
            </svg>
          </button>
        </div>
        <div v-if="lightBoxIndex > 0" class="vel-btn-custom btn__prev" @click="prevImage">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="20" height="20">
            <path d="M6 12H18M6 12L11 7M6 12L11 17" stroke="#ffffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </div>
        <div class="vel-img-wrapper">
          <Transition :name="slideDirection" mode="out-in">
            <img :key="lightBoxIndex" :src="lightboxImages[lightBoxIndex]?.src" :alt="lightboxImages[lightBoxIndex]?.title" class="vel-img" />
          </Transition>
        </div>
        <div v-if="lightBoxIndex < lightboxImages.length - 1" class="vel-btn-custom btn__next" @click="nextImage">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="20" height="20">
            <path d="M6 12H18M18 12L13 7M18 12L13 17" stroke="#ffffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </div>
        <div class="vel-img-title-custom tw-top-auto tw-bottom-4">
          <div v-if="lightboxImages[lightBoxIndex]?.title">{{ lightboxImages[lightBoxIndex]?.title }}</div>
          <div v-if="lightboxImages[lightBoxIndex]?.description" class="vel-img-description-custom tw-text-light-gray">{{ lightboxImages[lightBoxIndex]?.description }}</div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <div v-if="showLightBox && showThumbnails" class="vel-thumbnail-strip">
        <img
          v-for="(item, i) in lightboxImages"
          :key="i"
          :src="item.src"
          :alt="item.title"
          class="vel-thumbnail"
          :class="{ 'vel-thumbnail--active': i === lightBoxIndex }"
          @click="lightBoxIndex = i"
        />
      </div>
    </Teleport>

    <MasonryWall
      data-allow-mismatch
      :items="items"
      :column-width="imageColumnWidth"
      :max-columns="isMobile ? 2 : 3"
      :gap="16"
    >
      <template #default="{ item, index }">
        <MediaImageLazyLoad
          :src="item.image?.large || ''"
          :placeholder-src="item.image?.tiny || ''"
          :alt="item.title"
          class="tw-rounded-xl hover:tw-cursor-pointer"
          seo-optimize
          @click="onClickImage(item, index)"
        />
      </template>
    </MasonryWall>
  </div>
</template>

<script setup lang="ts">
import { useMediaQuery } from '@vueuse/core'

const props = withDefaults(defineProps<{
  items: GalleryMeta[]
  actionEventName?: string
  fbEventName?: string
  eventName?: string
}>(), {
  items: () => ([]),
  actionEventName: '',
  fbEventName: '',
  eventName: '',
})

const showLightBox = ref(false)
const lightBoxIndex = ref(0)
const slideDirection = ref('gallery-slide-next')
const isPlaying = ref(false)
const showThumbnails = ref(false)
const slideshowTimer = ref<ReturnType<typeof setInterval> | null>(null)
const titleTop = ref('auto')

const updateTitlePos = (retries = 20) => {
  nextTick(() => {
    const img = document.querySelector('.vel-img') as HTMLElement | null
    if (!img || img.getBoundingClientRect().height === 0) {
      if (retries > 0) setTimeout(() => updateTitlePos(retries - 1), 100)
      return
    }
    const rect = img.getBoundingClientRect()
    titleTop.value = `${rect.bottom + 32}px`
  })
}

const prevImage = () => {
  if (lightBoxIndex.value > 0) {
    slideDirection.value = 'gallery-slide-prev'
    lightBoxIndex.value--
    updateTitlePos()
  }
}

const nextImage = () => {
  if (lightBoxIndex.value < lightboxImages.value.length - 1) {
    slideDirection.value = 'gallery-slide-next'
    lightBoxIndex.value++
    updateTitlePos()
  }
}

const stopSlideshow = () => {
  if (slideshowTimer.value) {
    clearInterval(slideshowTimer.value)
    slideshowTimer.value = null
  }
  isPlaying.value = false
}

const toggleSlideshow = () => {
  if (isPlaying.value) {
    stopSlideshow()
  }
  else {
    isPlaying.value = true
    slideshowTimer.value = setInterval(() => {
      slideDirection.value = 'gallery-slide-next'
      if (lightBoxIndex.value < lightboxImages.value.length - 1) {
        lightBoxIndex.value++
      }
      else {
        lightBoxIndex.value = 0
      }
      updateTitlePos()
    }, 3000)
  }
}

const toggleThumbnails = () => {
  showThumbnails.value = !showThumbnails.value
}

const onHideLightBox = () => {
  showLightBox.value = false
  stopSlideshow()
  showThumbnails.value = false
}

const lightboxImages = computed(() =>
  props.items.map(item => ({
    title: item.title,
    description: item.description,
    src: item?.image?.large || '',
  })),
)

const onClickImage = (item: { image: ImageMeta, title: string, description: string }, index: number) => {
  lightBoxIndex.value = index
  showLightBox.value = true
  updateTitlePos()
  eventGalleryName(item.title)
}

const { analyticEvent, fbPixelEvent } = useAnalytic()
const eventGalleryName = (title: string) => {
  analyticEvent({
    ga4ActionName: `click_gallery_${props.eventName}`,
    ga4Label: title,
  })
  fbPixelEvent({
    fbCategory: 'trackCustom',
    fbEvent: props.fbEventName,
    fbValue: { label: title },
  })
}

const handleKeyDown = (e: KeyboardEvent) => {
  if (!showLightBox.value) { return }
  if (e.key === 'ArrowRight') { nextImage() }
  else if (e.key === 'ArrowLeft') { prevImage() }
  else if (e.key === 'Escape') { onHideLightBox() }
}

onMounted(() => { window.addEventListener('keydown', handleKeyDown) })
onUnmounted(() => { window.removeEventListener('keydown', handleKeyDown) })

const isMobile = useMediaQuery('(max-width: 599px)')
const imageColumnWidth = computed(() => isMobile.value ? 150 : 250)
</script>

<style scoped>
.vel-img-title-custom {
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: unset !important;
  font-size: 16px;
}

.vel-img-description-custom {
  color: #d1d5db !important;
  font-size: 15px;
}

.gallery-slide-next-enter-active,
.gallery-slide-next-leave-active,
.gallery-slide-prev-enter-active,
.gallery-slide-prev-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.gallery-slide-next-enter-from {
  opacity: 0;
  transform: translateX(48px);
}
.gallery-slide-next-leave-to {
  opacity: 0;
  transform: translateX(-48px);
}

.gallery-slide-prev-enter-from {
  opacity: 0;
  transform: translateX(-48px);
}
.gallery-slide-prev-leave-to {
  opacity: 0;
  transform: translateX(48px);
}
</style>
